import { DestroyRef, Directive, effect, EventEmitter, Inject, inject, InjectionToken, Output, signal, viewChild } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { IBaseModelAssetService } from '../../../@core/interfaces/IBaseModelAssetService.interface';
import { BaseListService } from '../../../@core/services/base-list.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { Asset, AssetInput, IBaseModel } from '../../../generated/generated-types';
import { AuthService } from '../../../shared/services/auth.service';
import { ReportService } from '../../printing/services/report.service';
import { WidgetAssetListComponent } from '../../products/components/widget-asset-list/widget-asset-list.component';
import { AssetService } from '../services/asset.service';

/**
 * Injection token used to provide and inject a service that implements both
 * BaseListService and IBaseModelAssetService. Required because interfaces
 * and type intersections cannot be injected directly in Angular.
 */
const CRUD_SERVICE_TOKEN = new InjectionToken<BaseListService & IBaseModelAssetService>('CrudServiceToken');

//not used as a directive, but as a base-class
@Directive({
  // selector: '[appWidgetAssetsManager]',
  standalone: true,
  // exportAs: 'appWidgetAssetsManager',
})
export abstract class WidgetAssetsManagerComponent<
  T extends IBaseModel,
  TService extends BaseListService = BaseListService
> extends FieldType<FieldTypeConfig> {
  protected authService = inject(AuthService);
  protected destroyRef = inject(DestroyRef);

  assetListWidget = viewChild(WidgetAssetListComponent);

  isComponentRendered = signal<boolean>(false);

  @Output()
  assetsAdded = new EventEmitter<Asset[]>();

  reportService: ReportService = inject(ReportService);

  constructor(
    @Inject(CRUD_SERVICE_TOKEN) public crudService: TService & IBaseModelAssetService,
    public assetService: AssetService,
    public notificationService: NotificationService,
    public i18n: I18nService,

  ) {
    super();

    this.destroyRef.onDestroy(() => {
      // console.log(`Destroying ${this.constructor.name}`);
    });

    effect(() => {
      if (this.assetListWidget()) {
        this.assetListWidget().modelService = this.crudService;
      }
    });

  }

  async createAssets(files: File[]): Promise<Asset[]> {

    if (!this.model)
      throw new Error('missing-model');

    if (!this.model.id)
      throw new Error('missing-model-id');

    if (files.length) {

      let uploadedAssets: Asset[] = [];

      for (let file of files) {

        if (!file) {
          this.notificationService.error(_("common.upload-error-no-file"));
          continue;
        }

        const compressedImage = await this.assetService.resizeImageIfNeeded(file);

        // if its no image file
        if (compressedImage) {
          file = compressedImage;
        }

        const input: AssetInput = {
          file: file,
          name: file.name
        }

        const res = await this.assetService.saveAsset(input);
        uploadedAssets.push(res);
      }

      if (uploadedAssets.length > 0) {

        await this.crudService.addAssets(this.model.id, uploadedAssets.map(x => x.id));

        this.assetListWidget().refresh();

        this.notificationService.success(_("product.assets-updated"));

        return uploadedAssets;
      } else {
        return [];
      }
    }
  }

}
